/**
 * Telemetry wrapper for the existing useMonitoring hook
 * This enhances the existing monitoring system with OpenTelemetry traces and metrics
 * Creates child spans under the main frontend trace for monitoring activities
 */

import { useCallback, useRef } from 'react';
import { useTelemetryMetrics } from '@/hooks/telemetry/useTelemetryMetrics';
import { ViolationType, Violation } from '@/types/video-call';
import { frontendTraceManager } from '@/telemetry/FrontendTraceManager';
import { SpanStatusCode } from '@/telemetry-browser';

// Global tracker to prevent duplicate monitoring telemetry instances
const globalMonitoringTracker = new Map<string, boolean>();

interface TelemetryWrapperOptions {
  callId?: string;
  organizationId?: string;
  callType?: 'voice_call' | 'video_interview';
  enableViolationTracking?: boolean;
  enableUserInteractionTracking?: boolean;
  enablePerformanceTracking?: boolean;
}

/**
 * Hook that wraps existing monitoring callbacks with telemetry
 * Adds monitoring events to the main frontend trace
 */
export function useMonitoringWithTelemetryWrapper(options: TelemetryWrapperOptions = {}) {
  const {
    callId,
    organizationId,
    callType = 'video_interview',
    enableViolationTracking = true,
    enableUserInteractionTracking = true,
    enablePerformanceTracking = true,
  } = options;

  const { recordViolation, recordUserInteraction, recordPerformanceMetric } = useTelemetryMetrics();
  
  // Track if monitoring telemetry is active for this instance
  const isActiveRef = useRef<boolean>(false);
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));

  // Initialize monitoring telemetry tracking
  const startMonitoringTelemetry = useCallback(() => {
    console.log(`🚀 [Monitoring] startMonitoringTelemetry called for callId: ${callId}, orgId: ${organizationId}`);
    
    const trackingKey = `${callId}-${organizationId}`;
    
    // Check local reference first
    if (isActiveRef.current) {
      console.log('📊 [Monitoring] Telemetry already active locally, reusing existing monitoring');
      return;
    }
    
    // Check global tracker to prevent duplicate monitoring across component instances
    const existingGlobalMonitoring = globalMonitoringTracker.get(trackingKey);
    if (existingGlobalMonitoring) {
      console.log('📊 [Monitoring] Telemetry already active globally, reusing existing monitoring');
      return;
    }

    console.log(`📊 [Monitoring] Starting monitoring telemetry as child spans under video call session: ${callId}`);
    
    // Create monitoring start child span for Tempo UI visibility
    const startSpan = frontendTraceManager.createChildSpan('video_call.monitoring_started', {
      attributes: {
        'video_call.id': callId || 'unknown',
        'video_call.type': callType,
        'video_call.organization_id': organizationId || 'unknown',
        'monitoring.start_time': new Date().toISOString(),
        'monitoring.instance_id': instanceId.current,
        'monitoring.tracking_key': trackingKey,
        'monitoring.violations_enabled': enableViolationTracking,
        'monitoring.interactions_enabled': enableUserInteractionTracking,
        'monitoring.performance_enabled': enablePerformanceTracking,
      },
    });
    
    if (startSpan) {
      startSpan.setStatus({ code: 1 }); // OK
      startSpan.end();
    }

    // Mark as active
    isActiveRef.current = true;
    globalMonitoringTracker.set(trackingKey, true);

    console.log(`✅ [Monitoring] Monitoring telemetry started for: ${callId} (${trackingKey})`);
  }, [callId, callType, organizationId, enableViolationTracking, enableUserInteractionTracking, enablePerformanceTracking]);

  // End monitoring telemetry tracking
  const endMonitoringTelemetry = useCallback((reason?: string) => {
    const trackingKey = `${callId}-${organizationId}`;
    
    if (!isActiveRef.current) {
      console.warn('📊 [Monitoring] No monitoring telemetry to end');
      return;
    }

    // Create monitoring end child span for Tempo UI visibility
    const endSpan = frontendTraceManager.createChildSpan('video_call.monitoring_ended', {
      attributes: {
        'video_call.id': callId || 'unknown',
        'monitoring.end_reason': reason || 'unknown',
        'monitoring.tracking_key': trackingKey,
        'monitoring.instance_id': instanceId.current,
        'monitoring.ended_at': new Date().toISOString(),
      },
    });
    
    if (endSpan) {
      endSpan.setStatus({ code: 1 }); // OK
      endSpan.end();
    }
    
    // Clean up tracking
    isActiveRef.current = false;
    globalMonitoringTracker.delete(trackingKey);
    
    console.log(`📊 [Monitoring] Ended monitoring telemetry: ${callId} (${reason}) - cleaned up ${trackingKey}`);
  }, [callId, organizationId]);

  // Helper function to create monitoring child spans for Tempo UI visibility
  const createMonitoringSpan = useCallback((spanName: string, attributes: Record<string, any> = {}) => {
    console.log(`🔧 [Monitoring] Creating child span: video_call.monitoring.${spanName}`, {
      callId,
      spanName,
      attributes,
      isActive: isActiveRef.current
    });
    
    // Create a short-lived child span for this monitoring event
    const span = frontendTraceManager.createChildSpan(`video_call.monitoring.${spanName}`, {
      attributes: {
        'video_call.id': callId || 'unknown',
        'video_call.type': callType,
        'video_call.organization_id': organizationId || 'unknown',
        'monitoring.instance_id': instanceId.current,
        'monitoring.timestamp': new Date().toISOString(),
        ...attributes,
      },
    });

    if (span) {
      // End the span immediately to create a visible event in the timeline
      span.setStatus({ code: 1 }); // OK
      span.end();
      console.log(`✅ [Monitoring] Child span created and closed: ${spanName}`);
    } else {
      console.warn(`❌ [Monitoring] Failed to create child span: ${spanName}`);
    }
  }, [callId, callType, organizationId]);

  // Enhanced violation handler with telemetry
  const wrapAddViolation = useCallback((
    originalAddViolation: (type: ViolationType, message: string, severity: Violation["severity"], duration?: number) => void
  ) => {
    return (type: ViolationType, message: string, severity: Violation["severity"] = "warning", duration?: number) => {
      console.log(`🚨 [Monitoring] wrapAddViolation called:`, {
        type, 
        message, 
        severity, 
        duration, 
        callId, 
        isActive: isActiveRef.current
      });
      
      try {
        // Create violation child span for Tempo UI visibility
        createMonitoringSpan(`violation.${type}`, {
          'violation.type': type,
          'violation.severity': severity,
          'violation.message': message,
          'violation.duration': duration || 0,
          'violation.source': 'monitoring_wrapper',
        });

        // Record violation metric
        if (enableViolationTracking) {
          recordViolation(type, severity as any, {
            call_id: callId || 'unknown',
            call_type: callType,
            organization_id: organizationId || 'unknown',
            duration: duration || 0,
          });
        }

        // Record user interaction (violation events are user-triggered)
        if (enableUserInteractionTracking) {
          recordUserInteraction('violation_triggered', 'monitoring_system', {
            violation_type: type,
            severity: severity,
            call_id: callId || 'unknown',
          });
        }

        // Call the original function
        originalAddViolation(type, message, severity, duration);

        console.log(`✅ [Monitoring] Violation span recorded: ${type} (${severity}) - ${message}`);

      } catch (error) {
        console.error(`❌ [Monitoring] Failed to record violation: ${error}`);
        // Still call the original function even if telemetry fails
        originalAddViolation(type, message, severity, duration);
        throw error;
      }
    };
  }, [callId, organizationId, callType, enableViolationTracking, enableUserInteractionTracking, recordViolation, recordUserInteraction, createMonitoringSpan]);

  // Enhanced tab change penalty wrapper
  const wrapTabChangePenalty = useCallback((
    originalShowTabChangePenalty: () => void
  ) => {
    return () => {
      try {
        // Create tab change penalty child span for Tempo UI visibility
        createMonitoringSpan('penalty.tab_change', {
          'penalty.type': 'tab_change',
          'penalty.source': 'monitoring_wrapper',
        });

        // Record performance metric
        if (enablePerformanceTracking) {
          recordPerformanceMetric('tab_change_penalties', 1, 'count', {
            call_id: callId || 'unknown',
            call_type: callType,
          });
        }

        originalShowTabChangePenalty();
        
        console.log(`✅ [Monitoring] Tab change penalty span recorded for call: ${callId}`);

      } catch (error) {
        console.error(`❌ [Monitoring] Failed to record tab change penalty: ${error}`);
        originalShowTabChangePenalty(); // Still show penalty even if telemetry fails
        throw error;
      }
    };
  }, [callId, callType, enablePerformanceTracking, recordPerformanceMetric, createMonitoringSpan]);

  // Enhanced window blur penalty wrapper
  const wrapWindowBlurPenalty = useCallback((
    originalShowWindowBlurPenalty: () => void
  ) => {
    return () => {
      try {
        // Create window blur penalty child span for Tempo UI visibility
        createMonitoringSpan('penalty.window_blur', {
          'penalty.type': 'window_blur',
          'penalty.source': 'monitoring_wrapper',
        });

        // Record performance metric
        if (enablePerformanceTracking) {
          recordPerformanceMetric('window_blur_penalties', 1, 'count', {
            call_id: callId || 'unknown',
            call_type: callType,
          });
        }

        originalShowWindowBlurPenalty();
        
        console.log(`✅ [Monitoring] Window blur penalty span recorded for call: ${callId}`);

      } catch (error) {
        console.error(`❌ [Monitoring] Failed to record window blur penalty: ${error}`);
        originalShowWindowBlurPenalty(); // Still show penalty even if telemetry fails
        throw error;
      }
    };
  }, [callId, callType, enablePerformanceTracking, recordPerformanceMetric, createMonitoringSpan]);

  // Enhanced call pause wrapper
  const wrapPauseCall = useCallback((
    originalPauseCall: (reason: "monitor_detected") => void
  ) => {
    return (reason: "monitor_detected") => {
      try {
        // Create call pause child span for Tempo UI visibility
        createMonitoringSpan('call.paused', {
          'call.pause_reason': reason,
          'call.action': 'paused',
          'call.source': 'monitoring_wrapper',
        });

        // Record user interaction
        if (enableUserInteractionTracking) {
          recordUserInteraction('call_paused', 'monitoring_system', {
            reason: reason,
            call_id: callId || 'unknown',
            call_type: callType,
          });
        }

        originalPauseCall(reason);
        
        console.log(`✅ [Monitoring] Call pause span recorded: ${reason} for call: ${callId}`);

      } catch (error) {
        console.error(`❌ [Monitoring] Failed to record call pause: ${error}`);
        originalPauseCall(reason); // Still pause call even if telemetry fails
        throw error;
      }
    };
  }, [callId, callType, enableUserInteractionTracking, recordUserInteraction, createMonitoringSpan]);

  // Enhanced call resume wrapper
  const wrapResumeCall = useCallback((
    originalResumeCall: () => void
  ) => {
    return () => {
      try {
        // Create call resume child span for Tempo UI visibility
        createMonitoringSpan('call.resumed', {
          'call.action': 'resumed',
          'call.source': 'monitoring_wrapper',
        });

        // Record user interaction
        if (enableUserInteractionTracking) {
          recordUserInteraction('call_resumed', 'monitoring_system', {
            call_id: callId || 'unknown',
            call_type: callType,
          });
        }

        originalResumeCall();
        
        console.log(`✅ [Monitoring] Call resume span recorded for call: ${callId}`);

      } catch (error) {
        console.error(`❌ [Monitoring] Failed to record call resume: ${error}`);
        originalResumeCall(); // Still resume call even if telemetry fails
        throw error;
      }
    };
  }, [callId, callType, enableUserInteractionTracking, recordUserInteraction, createMonitoringSpan]);

  // Enhanced end call wrapper
  const wrapEndCall = useCallback((
    originalEndCall: (reason: string) => void
  ) => {
    return (reason: string) => {
      try {
        // Create call end child span for Tempo UI visibility
        createMonitoringSpan('call.ended', {
          'call.end_reason': reason,
          'call.action': 'ended',
          'call.source': 'monitoring_wrapper',
        });

        // Record user interaction
        if (enableUserInteractionTracking) {
          recordUserInteraction('call_ended', 'monitoring_system', {
            reason: reason,
            call_id: callId || 'unknown',
            call_type: callType,
          });
        }

        originalEndCall(reason);
        
        console.log(`✅ [Monitoring] Call end span recorded: ${reason} for call: ${callId}`);

      } catch (error) {
        console.error(`❌ [Monitoring] Failed to record call end: ${error}`);
        originalEndCall(reason); // Still end call even if telemetry fails
        throw error;
      }
    };
  }, [callId, callType, enableUserInteractionTracking, recordUserInteraction, createMonitoringSpan]);

  return {
    startMonitoringTelemetry,
    endMonitoringTelemetry,
    wrapAddViolation,
    wrapTabChangePenalty,
    wrapWindowBlurPenalty,
    wrapPauseCall,
    wrapResumeCall,
    wrapEndCall,
  };
}